import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { toast } from 'react-toastify';

/**
 * Interface para la solicitud de cambio de RUT
 */
export interface ChangeRutRequest {
  oldRut: string;
  newRut: string;
}

/**
 * Interface para la respuesta del cambio de RUT
 */
export interface ChangeRutResponse {
  rutAnterior: string;
  rutNuevo: string;
  persona: {
    rut: string;
    nombre: string;
    apellido: string;
    nombres: string;
    [key: string]: any;
  };
}

/**
 * Hook para cambiar el RUT de un personal
 * 
 * ⚠️ OPERACIÓN CRÍTICA: Este endpoint actualiza el RUT en todas las tablas relacionadas
 * mediante una transacción. Si falla alguna actualización, se hace rollback completo.
 * 
 * Validaciones requeridas:
 * - Formato de RUT válido
 * - RUT nuevo no debe existir
 * - Requiere permisos de administrador
 * - Requiere confirmación explícita del usuario
 */
export const useChangeRut = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ oldRut, newRut }: ChangeRutRequest): Promise<ChangeRutResponse> => {
      console.log('🔍 Iniciando cambio de RUT:', { oldRut, newRut });
      
      // Validar formato básico (el backend hace validación completa)
      if (!oldRut || !newRut) {
        throw new Error('Debe proporcionar RUT actual y nuevo RUT');
      }
      
      if (oldRut === newRut) {
        throw new Error('El nuevo RUT debe ser diferente al actual');
      }
      
      try {
        const response = await apiService.changeRut(oldRut, newRut);
        console.log('✅ RUT cambiado exitosamente:', response);
        return response.data as ChangeRutResponse;
      } catch (error: any) {
        console.error('❌ Error al cambiar RUT:', error);
        
        // Manejar errores específicos del backend
        if (error.response?.data?.error) {
          throw new Error(error.response.data.error);
        }
        
        throw new Error('Error al cambiar el RUT. Por favor, contacte al administrador.');
      }
    },
    onSuccess: (data, variables) => {
      // Invalidar todas las queries relacionadas con el RUT antiguo y nuevo
      queryClient.invalidateQueries({ queryKey: ['personal', variables.oldRut] });
      queryClient.invalidateQueries({ queryKey: ['personal', 'detail', variables.oldRut] });
      queryClient.invalidateQueries({ queryKey: ['personal', variables.newRut] });
      queryClient.invalidateQueries({ queryKey: ['personal', 'detail', variables.newRut] });
      queryClient.invalidateQueries({ queryKey: ['personal', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['personal'] });
      queryClient.invalidateQueries({ queryKey: ['personal', 'stats'] });
      
      // Invalidar queries relacionadas con tablas afectadas
      queryClient.invalidateQueries({ queryKey: ['documentos'] });
      queryClient.invalidateQueries({ queryKey: ['cursos'] });
      queryClient.invalidateQueries({ queryKey: ['estados'] });
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      queryClient.invalidateQueries({ queryKey: ['programacion'] });
      
      // Mostrar notificación de éxito
      toast.success(
        `RUT actualizado exitosamente: ${data.rutAnterior} → ${data.rutNuevo}`,
        { autoClose: 5000 }
      );
      
      // Emitir evento personalizado para que otros componentes puedan reaccionar
      try {
        const event = new CustomEvent('rutChanged', {
          detail: {
            oldRut: variables.oldRut,
            newRut: variables.newRut,
            persona: data.persona
          }
        });
        window.dispatchEvent(event);
      } catch (err) {
        console.error('Error al emitir evento rutChanged:', err);
      }
    },
    onError: (error: any) => {
      console.error('❌ Error en mutación de cambio de RUT:', error);
      toast.error(error.message || 'Error al cambiar el RUT', { autoClose: 5000 });
    },
  });
};

/**
 * Hook para validar si un RUT nuevo está disponible
 */
export const useValidateNewRut = () => {
  return useMutation({
    mutationFn: async (rut: string): Promise<{ available: boolean; message: string }> => {
      try {
        // Intentar obtener personal con el RUT
        await apiService.getPersonalByRut(rut);
        // Si no lanza error, el RUT ya existe
        return {
          available: false,
          message: 'El RUT ya existe en el sistema'
        };
      } catch (error: any) {
        if (error.response?.status === 404) {
          // 404 significa que el RUT no existe, está disponible
          return {
            available: true,
            message: 'RUT disponible'
          };
        }
        // Otro error (500, 403, etc.)
        throw new Error('Error al validar el RUT');
      }
    },
  });
};
